import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReportPdfColorModal from "../ReportPdfColorModal";
import axios from "axios";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
} from "@mui/material";

import {
  AssessmentOutlined,
  Close,
  DownloadOutlined,
  FilterAltOutlined,
  RestartAlt,
} from "@mui/icons-material";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import {
  DEFAULT_PDF_COLORS,
  usePdfSettings,
} from "../../../layouts/PdfSettingsContext.js";
import { useBranch } from "../../../layouts/BranchContext";


const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://wims.revisetoexcel.com";

const API = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

API.interceptors.request.use((config) => {
  const token =
    sessionStorage.getItem("accessToken") ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    localStorage.getItem("token");

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;
  }

  return config;
});

/* -------------------------------------------------------------------------- */
/* HELPERS                                                                    */
/* -------------------------------------------------------------------------- */

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};


const getAbsoluteImageUrl = (value) => {
  if (!value) return "";

  const imageValue =
    typeof value === "object"
      ? value?.url ||
        value?.image ||
        value?.path ||
        value?.src ||
        ""
      : value;

  if (!imageValue) return "";

  if (/^https?:\/\//i.test(String(imageValue))) {
    return String(imageValue);
  }

  if (String(imageValue).startsWith("//")) {
    return `https:${imageValue}`;
  }

  if (String(imageValue).startsWith("data:image/")) {
    return String(imageValue);
  }

  const base =
    String(BASE_URL)
      .replace(/\/api\/?$/i, "")
      .replace(/\/$/, "");

  return `${base}${
    String(imageValue).startsWith("/")
      ? ""
      : "/"
  }${imageValue}`;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader =
      new FileReader();

    reader.onloadend = () =>
      resolve(
        reader.result || ""
      );

    reader.onerror = reject;

    reader.readAsDataURL(blob);
  });

const imageUrlToDataUrl =
  async (url) => {
    if (!url) return "";

    if (
      String(url).startsWith(
        "data:image/"
      )
    ) {
      return String(url);
    }

    const response =
      await API.get(
        url,
        {
          responseType:
            "blob",
        }
      );

    return blobToDataUrl(
      response.data
    );
  };

const getUser = () => {
  for (const key of ["user", "userData", "currentUser"]) {
    try {
      const raw =
        sessionStorage.getItem(key) ||
        localStorage.getItem(key);

      if (raw) {
        return JSON.parse(raw) || {};
      }
    } catch (error) {
      console.error("User parse error:", error);
    }
  }

  return {};
};

const getTodayString = () =>
  new Date().toISOString().split("T")[0];

const getOneMonthAgoString = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return date.toISOString().split("T")[0];
};

const formatDate = (value) => {
  if (!value) return "—";

  // Date-only value ko timezone shift se bachane ke liye
  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value)
  ) {
    const [year, month, day] = value.split("-");

    return `${day}/${month}/${year}`;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-GB");
};

const formatValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    return value.map(formatValue).join(", ");
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .map(
        ([key, val]) =>
          `${labelize(key)}: ${formatValue(val)}`
      )
      .join(" | ");
  }

  return String(value);
};

const labelize = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const normalizeHex = (
  value,
  fallback = "#1E293B"
) => {
  let hex = String(value || fallback)
    .trim()
    .replace("#", "");

  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  if (
    hex.length !== 6 ||
    !/^[0-9a-fA-F]{6}$/.test(hex)
  ) {
    return fallback;
  }

  return `#${hex.toUpperCase()}`;
};

const hexToRgb = (hex) => {
  const safe = normalizeHex(hex).replace("#", "");

  return [
    parseInt(safe.slice(0, 2), 16),
    parseInt(safe.slice(2, 4), 16),
    parseInt(safe.slice(4, 6), 16),
  ];
};

const errorMessage = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 401) {
    return "Authentication failed. Please login again.";
  }

  if (status === 403) {
    return "You do not have permission to access this report.";
  }

  if (status === 404) {
    return "Report API endpoint was not found.";
  }

  if (typeof data === "string") {
    return data;
  }

  if (data?.detail) {
    return data.detail;
  }

  if (data?.message) {
    return data.message;
  }

  if (data?.error) {
    return formatValue(data.error);
  }

  return (
    error?.message ||
    "Something went wrong."
  );
};

const getBranchId = (branch) => {
  const rawId =
    branch?.id ??
    branch?.branch_id ??
    branch?.value ??
    branch?.branch?.id ??
    "";

  if (
    rawId &&
    typeof rawId === "object"
  ) {
    return rawId?.id ?? "";
  }

  return rawId;
};

const getBranchName = (branch) => {
  const name =
    branch?.name ??
    branch?.branch_name;

  if (
    typeof name === "string" ||
    typeof name === "number"
  ) {
    return String(name);
  }

  if (
    name &&
    typeof name === "object"
  ) {
    return (
      name?.name ||
      name?.branch_name ||
      (name?.id != null
        ? `Branch ${name.id}`
        : "Unknown Branch")
    );
  }

  return `Branch ${getBranchId(branch)}`;
};

const getAdmissionLabel = (item) => {
  const admissionCode =
    item?.admission_code ||
    item?.admissionNo ||
    item?.admission_no ||
    "";

  const studentName =
    item?.student_name ||
    item?.student ||
    item?.name ||
    "";

  if (
    admissionCode &&
    studentName
  ) {
    return `${admissionCode} - ${studentName}`;
  }

  return (
    admissionCode ||
    studentName ||
    "Unknown Admission"
  );
};

const getRegistrationCode = (item) =>
  item?.registration_code ||
  item?.registrationNo ||
  item?.registration_no ||
  item?.registration?.registration_code ||
  "";

const getRegistrationLabel = (item) => {
  const code =
    getRegistrationCode(item);

  const name =
    item?.candidate_name ||
    item?.student_name ||
    item?.student ||
    item?.name ||
    "";

  if (code && name) {
    return `${code} - ${name}`;
  }

  return (
    code ||
    name ||
    "Unknown Registration"
  );
};

const extractCourseName = (course) => {
  if (!course) return "";

  if (typeof course === "string") {
    return course;
  }

  return (
    course.course_name ||
    course.courses__course_name ||
    course.name ||
    course.title ||
    course.course ||
    ""
  );
};

const extractStudentCount = (course) => {
  if (!course) return 0;

  return (
    course.student_count ??
    course.count ??
    course.total ??
    0
  );
};

const normalizeCourses = (courses) => {
  if (!courses) return [];

  if (Array.isArray(courses)) {
    return courses;
  }

  if (typeof courses === "string") {
    return courses
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [courses];
};

const formatCoursesCell = (courses) => {
  const normalized =
    normalizeCourses(courses);

  if (!normalized.length) {
    return "—";
  }

  return (
    normalized
      .map((course) =>
        extractCourseName(course)
      )
      .filter(Boolean)
      .join(", ") || "—"
  );
};

const matchesSearch = (
  row,
  query
) => {
  if (!query) return true;

  return Object.values(row || {})
    .map(formatValue)
    .join(" ")
    .toLowerCase()
    .includes(
      query.toLowerCase()
    );
};

const normalizeAdmissionStatus = (
  value
) => {
  if (
    value === true ||
    value === 1 ||
    String(value).toLowerCase() ===
      "true" ||
    String(value).toLowerCase() ===
      "yes"
  ) {
    return "Yes";
  }

  if (
    value === false ||
    value === 0 ||
    String(value).toLowerCase() ===
      "false" ||
    String(value).toLowerCase() ===
      "no"
  ) {
    return "No";
  }

  return formatValue(value);
};

/* -------------------------------------------------------------------------- */
/* COLUMN DEFINITIONS                                                         */
/* -------------------------------------------------------------------------- */

const getAdmissionColumns = () => [
  "admission_code",
  "student_name",
  "email",
  "gender",
  "courses",
  "mobile_no",
  "admission_date",
  "status",
];

const getCourseColumns = () => [
  "course_code",
  "course_name",
  "male",
  "female",
  "total",
];

const getCancellationColumns = () => [
  "admissionNo",
  "cancellationDate",
  "reason",
];

const getRegistrationColumns = () => [
  "registration_code",
  "candidate_name",
  "mobile_no",
  "email",
  "gender",
  "registration_date",
  "organization",
  "branch",
  "courses",
  "is_admitted",
];

/* -------------------------------------------------------------------------- */
/* ROW MAPPERS                                                                */
/* -------------------------------------------------------------------------- */

const mapAdmissionRow = (
  item,
  index
) => ({
  id:
    item?.id ??
    `${index}-${item?.admission_code || "admission"}`,

  admission_code:
    item?.admission_code ||
    item?.admissionNo ||
    item?.admission_no ||
    "",

  student_name:
    item?.student_name ||
    item?.student ||
    item?.name ||
    "",

  email: item?.email || "",

  gender: item?.gender || "",

  courses: formatCoursesCell(
    item?.courses
  ),

  mobile_no:
    item?.mobile_no ||
    item?.mobile ||
    item?.phone ||
    "",

  admission_date:
    item?.admission_date ||
    item?.date ||
    "",

  status: item?.status || "",
});

const mapCourseRow = (
  item,
  index
) => ({
  id:
    item?.id ??
    `${index}-${item?.course_code || "course"}`,

  course_code:
    item?.course_code ||
    item?.code ||
    "",

  course_name:
    extractCourseName(item) ||
    item?.course_name ||
    "",

  male:
    item?.male ??
    item?.male_count ??
    item?.gender_split?.male ??
    0,

  female:
    item?.female ??
    item?.female_count ??
    item?.gender_split?.female ??
    0,

  total:
    item?.total ??
    item?.student_count ??
    item?.count ??
    0,
});

const mapCancellationRow = (
  item,
  index
) => ({
  id:
    item?.id ??
    `${index}-${item?.admissionNo || "cancel"}`,

  admissionNo:
    item?.admissionNo ||
    item?.admission_code ||
    "",

  cancellationDate:
    item?.cancellationDate ||
    item?.cancellation_date ||
    "",

  reason:
    item?.reason ||
    "",
});

const mapRegistrationRow = (
  item,
  index
) => ({
  id:
    item?.id ??
    `${index}-${getRegistrationCode(item) || "registration"}`,

  registration_code:
    getRegistrationCode(item),

  candidate_name:
    item?.candidate_name ||
    item?.student_name ||
    item?.student ||
    item?.name ||
    "",

  mobile_no:
    item?.mobile_no ||
    item?.mobile ||
    item?.phone ||
    "",

  email:
    item?.email || "",

  gender:
    item?.gender || "",

  registration_date:
    item?.registration_date ||
    item?.registrationDate ||
    item?.date ||
    "",

  organization:
    typeof item?.organization ===
    "object"
      ? item?.organization?.name ||
        item?.organization?.organization_name ||
        ""
      : item?.organization ||
        item?.organization_name ||
        "",

  branch:
    typeof item?.branch ===
    "object"
      ? item?.branch?.name ||
        item?.branch?.branch_name ||
        ""
      : item?.branch ||
        item?.branch_name ||
        "",

  courses:
    formatCoursesCell(
      item?.courses
    ),

  is_admitted:
    normalizeAdmissionStatus(
      item?.is_admitted
    ),
});

/* -------------------------------------------------------------------------- */
/* REPORT TABLE                                                               */
/* -------------------------------------------------------------------------- */

function ReportTable({ rows, columns, loading, hasLoaded = true }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const paginatedRows = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const displayValue = (row, column) => {
    if (column.toLowerCase().includes("date")) {
      return formatDate(row?.[column]);
    }
    return formatValue(row?.[column]);
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        mt: 2,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        overflowX: "auto",
        overflowY: "auto",
        maxHeight: 560,
        backgroundColor: "background.paper",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 10px 32px rgba(0,0,0,0.18)"
            : "0 10px 32px rgba(15,23,42,0.06)",
        "&::-webkit-scrollbar": {
          width: 7,
          height: 8,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#475569" : "#cbd5e1",
          borderRadius: 10,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#172033" : "#f1f5f9",
        },
      }}
    >
      <Table
        stickyHeader
        sx={{
          minWidth: 1050,
          tableLayout: "auto",
          "& .MuiTableCell-root": {
            borderColor: "divider",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                minWidth: 70,
                fontWeight: 700,
                whiteSpace: "nowrap",
                backgroundColor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              S.No
            </TableCell>
            {columns.map((column) => (
              <TableCell
                key={column}
                sx={{
                  minWidth: 145,
                  fontWeight: 700,
                  fontSize: ".82rem",
                  whiteSpace: "nowrap",
                  backgroundColor: "primary.main",
                  color: "primary.contrastText",
                  py: 1.65,
                  px: 2,
                  "&.MuiTableCell-stickyHeader": {
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                  },
                }}
              >
                {labelize(column)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 8 }}>
                <CircularProgress size={30} />
                <Typography variant="body2" sx={{ mt: 1.2, color: "text.primary" }}>
                  Loading report...
                </Typography>
              </TableCell>
            </TableRow>
          ) : !hasLoaded ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + 1}
                align="center"
                sx={{ py: 8 }}
              />
            </TableRow>
          ) : paginatedRows.length > 0 ? (
            paginatedRows.map((row, index) => (
              <TableRow
                hover
                key={row?.id ?? `${page}-${index}`}
                sx={{
                  "&:nth-of-type(even)": {
                    backgroundColor: "action.hover",
                  },
                  "&:hover": {
                    backgroundColor: "action.selected",
                  },
                  "& .MuiTableCell-root": {
                    py: 1.55,
                    px: 2,
                    fontSize: ".84rem",
                    whiteSpace: "nowrap",
                    color: "text.primary",
                  },
                }}
              >
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                {columns.map((column) => (
                  <TableCell key={column}>
                    {displayValue(row, column)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 8 }}>
                <AssessmentOutlined sx={{ fontSize: 42, color: "text.disabled", mb: 1 }} />
                <Typography fontWeight={600}>No records found</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: "text.primary" }}>
                  Try changing the filters or generating the report again.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{
          borderTop: 1,
          borderColor: "divider",
          backgroundColor: "background.paper",
          "& .MuiTablePagination-toolbar": {
            minHeight: 56,
            px: { xs: 1, md: 2 },
          },
        }}
      />
    </TableContainer>
  );
}

/* -------------------------------------------------------------------------- */
/* COLOR PICKER MODAL                                                         */
/* -------------------------------------------------------------------------- */

function ColorPickerModal({
  open,
  loading,
  tempThemeColor,
  tempTextColor,
  onClose,
  onThemeChange,
  onTextChange,
  onReset,
  onGenerate,
  reportTitle,
  }) {
  return (
    <ReportPdfColorModal
  open={open}
  onClose={onClose}
  themeColor={tempThemeColor}
  setThemeColor={onThemeChange}
  textColor={tempTextColor}
  setTextColor={onTextChange}
  reportTitle={reportTitle || "Report Title"}
  onGenerate={onGenerate}
  loading={loading}
  disabled={loading}
/>
  );
}

/* -------------------------------------------------------------------------- */
/* FILTER MODAL                                                               */
/* -------------------------------------------------------------------------- */

function FilterModal({
  open,
  onClose,
  title,
  globalBranchName,

  admissionType,
  onAdmissionTypeChange,
  selectedAdmissionNo,
  onSelectedAdmissionNoChange,
  admissionOptions,
  loadingAdmissions,

  courseType,
  onCourseTypeChange,
  selectedCourse,
  onSelectedCourseChange,
  courseOptions,
  loadingCourses,

  registrationType,
  onRegistrationTypeChange,
  selectedRegistrationNo,
  onSelectedRegistrationNoChange,
  registrationOptions,
  loadingRegistrations,

  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,

  showCourseFilter,
  showAdmissionFilter,
  showRegistrationFilter,

  cancellationType,
  onCancellationTypeChange,

  onReset,
  onApply,
}) {
  const isCancellation =
    title ===
    "Admission Cancellation Report";

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropProps={{
        style: {
          backgroundColor:
            "rgba(0,0,0,0.8)",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform:
            "translate(-50%, -50%)",
          width: { xs: "94%", sm: 650, md: 820 },
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          color: "text.primary",
          borderRadius: 3,
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 20px 60px rgba(0,0,0,0.55)"
              : "0 20px 60px rgba(15,23,42,0.18)",
          border: (theme) =>
            theme.palette.mode === "dark"
              ? `1px solid ${theme.palette.divider}`
              : "none",
          outline: "none",
          p: { xs: 2, sm: 3 },
          "&::-webkit-scrollbar": {
            width: 7,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "text.disabled",
            borderRadius: 10,
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mb: 2.5,
            pb: 1.5,
            borderBottom: 1,
            borderColor: "divider",
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "background.paper",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 18, sm: 20 },
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              {title} Filters
            </Typography>
            <Typography variant="caption" sx={{ color: "text.primary" }}>
              Select the required criteria and apply the report.
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              color: "text.primary",
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: {
                xs: "100%",
                md: "calc(50% - 8px)",
              },
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Global Branch"
              value={globalBranchName}
              disabled
            />
          </Box>

          {showAdmissionFilter && (
            <>
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 1 }}
                >
                  Admission
                </Typography>

                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  spacing={2}
                >
                  <Button
                    variant={
                      admissionType ===
                      "allAdmission"
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() =>
                      onAdmissionTypeChange(
                        "allAdmission"
                      )
                    }
                  >
                    All Admission
                  </Button>

                  <Button
                    variant={
                      admissionType ===
                      "selectedAdmission"
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() =>
                      onAdmissionTypeChange(
                        "selectedAdmission"
                      )
                    }
                  >
                    Selected Admission No.
                  </Button>
                </Stack>
              </Box>

              {admissionType ===
                "selectedAdmission" && (
                <Box sx={{ width: "100%" }}>
                  <FormControl
                    fullWidth
                    size="small"
                  >
                    <InputLabel>
                      Admission No.
                    </InputLabel>

                    <Select
                      value={
                        selectedAdmissionNo
                      }
                      label="Admission No."
                      onChange={(
                        event
                      ) =>
                        onSelectedAdmissionNoChange(
                          event.target
                            .value
                        )
                      }
                    >
                      <MenuItem value="">
                        Select Admission
                        No.
                      </MenuItem>

                      {loadingAdmissions ? (
                        <MenuItem
                          disabled
                        >
                          Loading...
                        </MenuItem>
                      ) : admissionOptions.length >
                        0 ? (
                        admissionOptions.map(
                          (item) => (
                            <MenuItem
                              key={
                                item.id ||
                                item.admission_code
                              }
                              value={
                                item.admission_code
                              }
                            >
                              {getAdmissionLabel(
                                item
                              )}
                            </MenuItem>
                          )
                        )
                      ) : (
                        <MenuItem
                          disabled
                        >
                          No Admission
                          Found
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </>
          )}

          {showCourseFilter && (
            <>
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 1 }}
                >
                  Course
                </Typography>

                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  spacing={2}
                >
                  <Button
                    variant={
                      courseType ===
                      "allCourse"
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() =>
                      onCourseTypeChange(
                        "allCourse"
                      )
                    }
                  >
                    All Course
                  </Button>

                  <Button
                    variant={
                      courseType ===
                      "selectedCourse"
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() =>
                      onCourseTypeChange(
                        "selectedCourse"
                      )
                    }
                  >
                    Selected Course
                  </Button>
                </Stack>
              </Box>

              {courseType ===
                "selectedCourse" && (
                <Box sx={{ width: "100%" }}>
                  <FormControl
                    fullWidth
                    size="small"
                  >
                    <InputLabel>
                      Course
                    </InputLabel>

                    <Select
                      value={
                        selectedCourse
                      }
                      label="Course"
                      onChange={(
                        event
                      ) =>
                        onSelectedCourseChange(
                          event.target
                            .value
                        )
                      }
                    >
                      <MenuItem value="">
                        Select Course
                      </MenuItem>

                      {loadingCourses ? (
                        <MenuItem
                          disabled
                        >
                          Loading...
                        </MenuItem>
                      ) : courseOptions.length >
                        0 ? (
                        courseOptions.map(
                          (
                            course
                          ) => (
                            <MenuItem
                              key={
                                course.id
                              }
                              value={
                                course.id
                              }
                            >
                              {extractCourseName(
                                course
                              )}
                            </MenuItem>
                          )
                        )
                      ) : (
                        <MenuItem
                          disabled
                        >
                          No Course
                          Found
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </>
          )}

          {showRegistrationFilter && (
            <>
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 1 }}
                >
                  Registration
                </Typography>

                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  spacing={2}
                >
                  <Button
                    variant={
                      registrationType ===
                      "allRegistration"
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() =>
                      onRegistrationTypeChange(
                        "allRegistration"
                      )
                    }
                  >
                    All Registration
                  </Button>

                  <Button
                    variant={
                      registrationType ===
                      "selectedRegistration"
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() =>
                      onRegistrationTypeChange(
                        "selectedRegistration"
                      )
                    }
                  >
                    Selected Registration No.
                  </Button>
                </Stack>
              </Box>

              {registrationType ===
                "selectedRegistration" && (
                <Box sx={{ width: "100%" }}>
                  <FormControl
                    fullWidth
                    size="small"
                  >
                    <InputLabel>
                      Registration No.
                    </InputLabel>

                    <Select
                      value={
                        selectedRegistrationNo
                      }
                      label="Registration No."
                      onChange={(
                        event
                      ) =>
                        onSelectedRegistrationNoChange(
                          event.target
                            .value
                        )
                      }
                    >
                      <MenuItem value="">
                        Select Registration
                        No.
                      </MenuItem>

                      {loadingRegistrations ? (
                        <MenuItem
                          disabled
                        >
                          Loading...
                        </MenuItem>
                      ) : registrationOptions.length >
                        0 ? (
                        registrationOptions.map(
                          (
                            item
                          ) => (
                            <MenuItem
                              key={
                                item.id ||
                                getRegistrationCode(
                                  item
                                )
                              }
                              value={getRegistrationCode(
                                item
                              )}
                            >
                              {getRegistrationLabel(
                                item
                              )}
                            </MenuItem>
                          )
                        )
                      ) : (
                        <MenuItem
                          disabled
                        >
                          No Registration
                          Found
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </>
          )}

          {isCancellation && (
            <>
              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 1 }}
                >
                  Cancellation
                </Typography>

                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  spacing={2}
                >
                  <Button
                    variant={
                      cancellationType ===
                      "allCancellation"
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() =>
                      onCancellationTypeChange(
                        "allCancellation"
                      )
                    }
                  >
                    All Cancellation
                  </Button>

                  <Button
                    variant={
                      cancellationType ===
                      "selectedAdmission"
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() =>
                      onCancellationTypeChange(
                        "selectedAdmission"
                      )
                    }
                  >
                    Selected Admission No.
                  </Button>
                </Stack>
              </Box>

              {cancellationType ===
                "selectedAdmission" && (
                <Box sx={{ width: "100%" }}>
                  <FormControl
                    fullWidth
                    size="small"
                  >
                    <InputLabel>
                      Admission No.
                    </InputLabel>

                    <Select
                      value={
                        selectedAdmissionNo
                      }
                      label="Admission No."
                      onChange={(
                        event
                      ) =>
                        onSelectedAdmissionNoChange(
                          event.target
                            .value
                        )
                      }
                    >
                      <MenuItem value="">
                        Select Admission
                        No.
                      </MenuItem>

                      {admissionOptions.map(
                        (
                          item
                        ) => (
                          <MenuItem
                            key={
                              item.id ||
                              item.admission_code
                            }
                            value={
                              item.admission_code
                            }
                          >
                            {getAdmissionLabel(
                              item
                            )}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </>
          )}

          <Box sx={{ width: { xs: "100%", md: "calc(50% - 8px)" } }}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(event) =>
                onFromDateChange(
                  event.target.value
                )
              }
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max:
                  toDate ||
                  undefined,
              }}
            />
          </Box>

          <Box sx={{ width: { xs: "100%", md: "calc(50% - 8px)" } }}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              type="date"
              value={toDate}
              onChange={(event) =>
                onToDateChange(
                  event.target.value
                )
              }
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </Box>

        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1.5}
          sx={{
            mt: 3,
            position: "sticky",
            bottom: 0,
            zIndex: 3,
            py: 1.5,
            backgroundColor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            variant="outlined"
            startIcon={
              <RestartAlt />
            }
            onClick={onReset}
          >
            Reset
          </Button>

          <Button
            variant="contained"
            startIcon={
              <FilterAltOutlined />
            }
            onClick={onApply}
          >
            Apply Filters
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* MAIN COMPONENT                                                             */
/* -------------------------------------------------------------------------- */

export default function AdmissionReport() {
  const [reportTab, setReportTab] = useState("admission");
  const user = useMemo(
    () => getUser(),
    []
  );

  const organizationId =
    user?.organization_id ||
    user?.organization?.id ||
    "";

  const organizationName =
    user?.organization_name ||
    user?.organizationName ||
    user?.organization?.name ||
    "Organization";

  const {
    selectedBranchId,
    branchList,
    organizationId: contextOrganizationId,
  } = useBranch();

  const globalBranchId =
    selectedBranchId ||
    user?.branch_id ||
    "";

  const effectiveOrganizationId =
    contextOrganizationId ||
    organizationId ||
    "";

  /* ------------------------------------------------------------------------ */
  /* DYNAMIC GLOBAL BRANCH DETAILS / LOGO                                    */
  /* ------------------------------------------------------------------------ */

  const [
    branchDetails,
    setBranchDetails,
  ] = useState(null);

  const [
    branchLogo,
    setBranchLogo,
  ] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadBranchDetails =
      async () => {
        if (!globalBranchId) {
          setBranchDetails(null);
          setBranchLogo("");
          return;
        }

        try {
          const response =
            await API.get(
              `/api/master/branch/${globalBranchId}/`
            );

          const data =
            response.data || {};

          if (!cancelled) {
            setBranchDetails(
              data
            );
          }

          const image =
            data?.image ||
            data?.logo ||
            data?.branch_logo ||
            data?.branchLogo ||
            "";

          if (image) {
            try {
              const logoDataUrl =
                await imageUrlToDataUrl(
                  getAbsoluteImageUrl(
                    image
                  )
                );

              if (!cancelled) {
                setBranchLogo(
                  logoDataUrl
                );
              }
            } catch (logoError) {
              console.error(
                "Branch logo conversion error:",
                logoError
              );

              if (!cancelled) {
                setBranchLogo("");
              }
            }
          } else if (!cancelled) {
            setBranchLogo("");
          }
        } catch (branchError) {
          console.error(
            "Branch details API error:",
            branchError
          );

          if (!cancelled) {
            setBranchDetails(null);
            setBranchLogo("");
          }
        }
      };

    loadBranchDetails();

    return () => {
      cancelled = true;
    };
  }, [globalBranchId]);


  const globalBranchName = useMemo(() => {
    if (!globalBranchId) {
      return "No branch selected";
    }

    const selectedBranch =
      (branchList || []).find(
        (item) =>
          String(getBranchId(item)) ===
          String(globalBranchId)
      );

    return selectedBranch
      ? getBranchName(selectedBranch)
      : branchDetails?.name ||
        branchDetails?.branch_name ||
        `Branch ${globalBranchId}`;
  }, [
    branchList,
    globalBranchId,
    branchDetails,
  ]);

  const {
    pdfThemeColor,
    pdfTextColor,
    updatePdfThemeColor,
    updatePdfTextColor,
  } = usePdfSettings();

  /* ------------------------------------------------------------------------ */
  /* ADMISSION STATES                                                         */
  /* ------------------------------------------------------------------------ */

  const [admissionRows, setAdmissionRows] =
    useState([]);

  const [
    admissionSearch,
    setAdmissionSearch,
  ] = useState("");

  const [
    admissionLoading,
    setAdmissionLoading,
  ] = useState(false);
  const [
    admissionHasLoaded,
    setAdmissionHasLoaded,
  ] = useState(false);

  const [
    admissionOptions,
    setAdmissionOptions,
  ] = useState([]);

  const [
    loadingAdmissions,
    setLoadingAdmissions,
  ] = useState(false);

  const [
    admissionFilters,
    setAdmissionFilters,
  ] = useState({
    admissionType:
      "allAdmission",
    selectedAdmissionNo: "",
    fromDate:
      getOneMonthAgoString(),
    toDate:
      getTodayString(),
  });

  /* ------------------------------------------------------------------------ */
  /* COURSE STATES                                                            */
  /* ------------------------------------------------------------------------ */

  const [courseRows, setCourseRows] =
    useState([]);

  const [
    courseSearch,
    setCourseSearch,
  ] = useState("");

  const [
    courseLoading,
    setCourseLoading,
  ] = useState(false);
  const [
    courseHasLoaded,
    setCourseHasLoaded,
  ] = useState(false);

  const [
    courseOptions,
    setCourseOptions,
  ] = useState([]);

  const [
    loadingCourses,
    setLoadingCourses,
  ] = useState(false);

  const [
    courseFilters,
    setCourseFilters,
  ] = useState({
    admissionType:
      "allAdmission",
    selectedAdmissionNo: "",
    courseType: "allCourse",
    selectedCourse: "",
    fromDate:
      getOneMonthAgoString(),
    toDate:
      getTodayString(),
  });

  /* ------------------------------------------------------------------------ */
  /* CANCELLATION STATES                                                      */
  /* ------------------------------------------------------------------------ */

  const initialCancellationRows =
    useMemo(
      () => [
        {
          id: 1,
          admissionNo:
            "ADM001",
          cancellationDate:
            "2025-09-01",
          reason:
            "Withdrawn",
        },
        {
          id: 2,
          admissionNo:
            "ADM002",
          cancellationDate:
            "2025-09-02",
          reason:
            "Transferred",
        },
        {
          id: 3,
          admissionNo:
            "ADM003",
          cancellationDate:
            "2025-09-03",
          reason:
            "Personal Reasons",
        },
        {
          id: 4,
          admissionNo:
            "ADM004",
          cancellationDate:
            "2025-09-04",
          reason:
            "Financial Issues",
        },
        {
          id: 5,
          admissionNo:
            "ADM005",
          cancellationDate:
            "2025-09-05",
          reason:
            "Relocated",
        },
      ],
      []
    );

  const [
    cancellationRows,
    setCancellationRows,
  ] = useState(
    initialCancellationRows
  );

  const [
    cancellationSearch,
    setCancellationSearch,
  ] = useState("");

  const [
    cancellationFilters,
    setCancellationFilters,
  ] = useState({
    cancellationType:
      "allCancellation",
    selectedAdmissionNo: "",
    fromDate:
      getOneMonthAgoString(),
    toDate:
      getTodayString(),
  });

  /* ------------------------------------------------------------------------ */
  /* REGISTRATION STATES                                                      */
  /* ------------------------------------------------------------------------ */

  const [
    registrationRows,
    setRegistrationRows,
  ] = useState([]);

  const [
    registrationSearch,
    setRegistrationSearch,
  ] = useState("");

  const [
    registrationLoading,
    setRegistrationLoading,
  ] = useState(false);
  const [
    registrationHasLoaded,
    setRegistrationHasLoaded,
  ] = useState(false);

  const [
    registrationOptions,
    setRegistrationOptions,
  ] = useState([]);

  const [
    loadingRegistrations,
    setLoadingRegistrations,
  ] = useState(false);

  const [
    registrationFilters,
    setRegistrationFilters,
  ] = useState({
    registrationType:
      "allRegistration",
    selectedRegistrationNo:
      "",
    fromDate:
      getOneMonthAgoString(),
    toDate:
      getTodayString(),
  });

  /* ------------------------------------------------------------------------ */
  /* COMMON UI STATES                                                         */
  /* ------------------------------------------------------------------------ */

  const [error, setError] =
    useState("");

  const [
    admissionFilterOpen,
    setAdmissionFilterOpen,
  ] = useState(false);

  const [
    courseFilterOpen,
    setCourseFilterOpen,
  ] = useState(false);

  const [
    cancellationFilterOpen,
    setCancellationFilterOpen,
  ] = useState(false);

  const [
    registrationFilterOpen,
    setRegistrationFilterOpen,
  ] = useState(false);
  const [
    colorModalOpen,
    setColorModalOpen,
  ] = useState(null);

  const [
    tempPdfThemeColor,
    setTempPdfThemeColor,
  ] = useState(
    normalizeHex(
      pdfThemeColor,
      "#1E293B"
    )
  );

  const [
    tempPdfTextColor,
    setTempPdfTextColor,
  ] = useState(
    normalizeHex(
      pdfTextColor,
      "#0F172A"
    )
  );

  const [pdfLoading, setPdfLoading] =
    useState(false);

  const [
    downloadLoading,
    setDownloadLoading,
  ] = useState(false);

  const admissionFromRef =
    useRef(null);

  const admissionToRef =
    useRef(null);

  const courseFromRef =
    useRef(null);

  const courseToRef =
    useRef(null);

  const cancellationFromRef =
    useRef(null);

  const cancellationToRef =
    useRef(null);

  const registrationFromRef =
    useRef(null);

  const registrationToRef =
    useRef(null);

  useEffect(() => {
    if (!colorModalOpen) return;

    setTempPdfThemeColor(
      normalizeHex(
        pdfThemeColor,
        "#1E293B"
      )
    );

    setTempPdfTextColor(
      normalizeHex(
        pdfTextColor,
        "#0F172A"
      )
    );
  }, [
    colorModalOpen,
    pdfThemeColor,
    pdfTextColor,
  ]);

  const notifyError =
    useCallback(
      (message) => {
        setError(
          message ||
            "Something went wrong."
        );
      },
      []
    );

  /* ------------------------------------------------------------------------ */
  /* ADMISSION OPTIONS                                                        */
  /* ------------------------------------------------------------------------ */

  const loadAdmissionOptions =
    useCallback(async () => {
      if (
        !effectiveOrganizationId ||
        !globalBranchId
      ) {
        setAdmissionOptions([]);
        return;
      }

      setLoadingAdmissions(true);

      try {
        const response =
          await API.get(
            "/api/admission/admissions/",
            {
              params: {
                organization_id:
                  effectiveOrganizationId,
                branch_id:
                  globalBranchId,
              },
            }
          );

        setAdmissionOptions(
          normalizeArray(
            response.data
          )
        );
      } catch (fetchError) {
        console.error(
          "Admission options error:",
          fetchError
        );

        setAdmissionOptions([]);
      } finally {
        setLoadingAdmissions(false);
      }
    }, [
      effectiveOrganizationId,
      globalBranchId,
    ]);

  useEffect(() => {
    if (
      admissionFilters.admissionType ===
      "selectedAdmission"
    ) {
      loadAdmissionOptions();
    }
  }, [
    admissionFilters.admissionType,
    loadAdmissionOptions,
  ]);

  /* ------------------------------------------------------------------------ */
  /* COURSE OPTIONS                                                           */
  /* ------------------------------------------------------------------------ */

  const loadCourseOptions =
    useCallback(async () => {
      if (
        !globalBranchId
      ) {
        setCourseOptions([]);
        return;
      }

      setLoadingCourses(true);

      try {
        const response =
          await API.get(
            "/api/course/courses/",
            {
              params: {
                branch:
                  globalBranchId,
              },
            }
          );

        setCourseOptions(
          normalizeArray(
            response.data
          )
        );
      } catch (fetchError) {
        console.error(
          "Course options error:",
          fetchError
        );

        setCourseOptions([]);
      } finally {
        setLoadingCourses(false);
      }
    }, [
      globalBranchId,
    ]);

  useEffect(() => {
    if (
      courseFilters.courseType ===
      "selectedCourse"
    ) {
      loadCourseOptions();
    }
  }, [
    courseFilters.courseType,
    loadCourseOptions,
  ]);

  /* ------------------------------------------------------------------------ */
  /* REGISTRATION OPTIONS                                                     */
  /* ------------------------------------------------------------------------ */

  const loadRegistrationOptions =
    useCallback(async () => {
      if (
        !effectiveOrganizationId ||
        !globalBranchId
      ) {
        setRegistrationOptions(
          []
        );
        return;
      }

      setLoadingRegistrations(
        true
      );

      try {
        const response =
          await API.get(
            "/api/student_details/registrations/",
            {
              params: {
                organization:
                  effectiveOrganizationId,
                branch:
                  globalBranchId,
              },
            }
          );

        const list =
          normalizeArray(
            response.data
          );

        setRegistrationOptions(
          list
        );
      } catch (fetchError) {
        console.error(
          "Registration options error:",
          fetchError
        );

        setRegistrationOptions(
          []
        );
      } finally {
        setLoadingRegistrations(
          false
        );
      }
    }, [
      effectiveOrganizationId,
      globalBranchId,
    ]);

  useEffect(() => {
    if (
      registrationFilters.registrationType ===
      "selectedRegistration"
    ) {
      loadRegistrationOptions();
    }
  }, [
    registrationFilters.registrationType,
    loadRegistrationOptions,
  ]);

  /* ------------------------------------------------------------------------ */
  /* PAYLOADS                                                                 */
  /* ------------------------------------------------------------------------ */

  const admissionPayload =
    useMemo(
      () => ({
        fromDate:
          admissionFilters.fromDate,

        toDate:
          admissionFilters.toDate,

        organizationId: effectiveOrganizationId,

        branch:
          globalBranchId,

        admissionNo:
          admissionFilters.admissionType ===
          "selectedAdmission"
            ? admissionFilters.selectedAdmissionNo
            : null,

        status: null,

        gender: null,
      }),
      [
        admissionFilters,
        effectiveOrganizationId,
        globalBranchId,
      ]
    );

  const coursePayload =
    useMemo(
      () => ({
        fromDate:
          courseFilters.fromDate,

        toDate:
          courseFilters.toDate,

        organizationId: effectiveOrganizationId,

        branch:
          globalBranchId,

        courseId:
          courseFilters.courseType ===
            "selectedCourse" &&
          courseFilters.selectedCourse
            ? Number(
                courseFilters.selectedCourse
              )
            : null,
      }),
      [
        courseFilters,
        effectiveOrganizationId,
        globalBranchId,
      ]
    );

  const registrationPayload =
    useMemo(
      () => ({
        fromDate:
          registrationFilters.fromDate,

        toDate:
          registrationFilters.toDate,

        organizationId: effectiveOrganizationId,

        branch:
          globalBranchId,

        registrationNo:
          registrationFilters.registrationType ===
          "selectedRegistration"
            ? registrationFilters.selectedRegistrationNo
            : null,
      }),
      [
        registrationFilters,
        effectiveOrganizationId,
        globalBranchId,
      ]
    );

  /* ------------------------------------------------------------------------ */
  /* FETCH ADMISSION REPORT                                                   */
  /* ------------------------------------------------------------------------ */

  const fetchAdmissionReport =
    useCallback(async () => {
      if (!effectiveOrganizationId) {
        notifyError(
          "Organization ID not found."
        );
        return [];
      }

      setAdmissionLoading(true);
      setAdmissionHasLoaded(false);
      setError("");

      try {
        const response =
          await API.post(
            "/api/reports/admission-report/",
            admissionPayload
          );

        const results =
          normalizeArray(
            response.data?.results
          );

        const rows =
          results.map(
            mapAdmissionRow
          );

        setAdmissionRows(rows);

        if (!rows.length) {
          setError(
            "No admission records found for the selected filters."
          );
        }

        return rows;
      } catch (fetchError) {
        console.error(
          "Admission report error:",
          fetchError
        );

        setAdmissionRows([]);

        notifyError(
          errorMessage(
            fetchError
          )
        );

        return [];
      } finally {
        setAdmissionLoading(
          false
        );
        setAdmissionHasLoaded(true);
      }
    }, [
      admissionPayload,
      notifyError,
      effectiveOrganizationId,
    ]);

  /* ------------------------------------------------------------------------ */
  /* FETCH COURSE REPORT                                                      */
  /* ------------------------------------------------------------------------ */

  const fetchCourseReport =
    useCallback(async () => {
      if (!effectiveOrganizationId) {
        notifyError(
          "Organization ID not found."
        );
        return [];
      }

      setCourseLoading(true);
      setCourseHasLoaded(false);
      setError("");

      try {
        const response =
          await API.post(
            "/api/reports/course-wise-admission/",
            coursePayload
          );

        const results =
          normalizeArray(
            response.data?.results ||
              response.data
          );

        const rows =
          results.map(
            mapCourseRow
          );

        setCourseRows(rows);

        if (!rows.length) {
          setError(
            "No course-wise admission records found for the selected filters."
          );
        }

        return rows;
      } catch (fetchError) {
        console.error(
          "Course report error:",
          fetchError
        );

        setCourseRows([]);

        notifyError(
          errorMessage(
            fetchError
          )
        );

        return [];
      } finally {
        setCourseLoading(
          false
        );
        setCourseHasLoaded(true);
      }
    }, [
      coursePayload,
      notifyError,
      effectiveOrganizationId,
    ]);

  /* ------------------------------------------------------------------------ */
  /* FETCH REGISTRATION REPORT                                                */
  /* ------------------------------------------------------------------------ */

  const fetchRegistrationReport =
    useCallback(async () => {
      if (!effectiveOrganizationId) {
        notifyError(
          "Organization ID not found."
        );
        return [];
      }

      setRegistrationLoading(
        true
      );
      setRegistrationHasLoaded(false);

      setError("");

      try {
        const response =
          await API.post(
            "/api/reports/registration-report/",
            registrationPayload
          );

        const results =
          normalizeArray(
            response.data?.results
          );

        const rows =
          results.map(
            mapRegistrationRow
          );

        setRegistrationRows(
          rows
        );

        if (!rows.length) {
          setError(
            "No registration records found for the selected filters."
          );
        }

        return rows;
      } catch (fetchError) {
        console.error(
          "Registration report error:",
          fetchError
        );

        setRegistrationRows(
          []
        );

        notifyError(
          errorMessage(
            fetchError
          )
        );

        return [];
      } finally {
        setRegistrationLoading(
          false
        );
        setRegistrationHasLoaded(true);
      }
    }, [
      effectiveOrganizationId,
      registrationPayload,
      notifyError,
    ]);

  /* ------------------------------------------------------------------------ */
  /* RESET FUNCTIONS                                                          */
  /* ------------------------------------------------------------------------ */

  const resetAdmissionFilters =
    useCallback(() => {
      setAdmissionFilters({
        admissionType:
          "allAdmission",
        selectedAdmissionNo:
          "",
        fromDate:
          getOneMonthAgoString(),
        toDate:
          getTodayString(),
      });

      setAdmissionSearch("");
      setAdmissionRows([]);
      setAdmissionHasLoaded(false);
    }, [
    ]);

  const resetCourseFilters =
    useCallback(() => {
      setCourseFilters({
        admissionType:
          "allAdmission",
        selectedAdmissionNo:
          "",
        courseType:
          "allCourse",
        selectedCourse:
          "",
        fromDate:
          getOneMonthAgoString(),
        toDate:
          getTodayString(),
      });

      setCourseSearch("");
      setCourseRows([]);
      setCourseHasLoaded(false);
    }, [
    ]);

  const resetCancellationFilters =
    useCallback(() => {
      setCancellationFilters({
        cancellationType:
          "allCancellation",
        selectedAdmissionNo:
          "",
        fromDate:
          getOneMonthAgoString(),
        toDate:
          getTodayString(),
      });

      setCancellationSearch("");

      setCancellationRows(
        initialCancellationRows
      );
    }, [
      initialCancellationRows,
    ]);

  const resetRegistrationFilters =
    useCallback(() => {
      setRegistrationFilters({
        registrationType:
          "allRegistration",
        selectedRegistrationNo:
          "",
        fromDate:
          getOneMonthAgoString(),
        toDate:
          getTodayString(),
      });

      setRegistrationSearch("");
      setRegistrationRows([]);
      setRegistrationHasLoaded(false);
    }, [
    ]);

  /* ------------------------------------------------------------------------ */
  /* GENERATE REPORT + OPEN PDF COLOR PICKER                                 */
  /* ------------------------------------------------------------------------ */

  const openPdfColorPicker = useCallback(
    (reportType) => {
      setTempPdfThemeColor(
        normalizeHex(
          pdfThemeColor,
          DEFAULT_PDF_COLORS.primary
        )
      );

      setTempPdfTextColor(
        normalizeHex(
          pdfTextColor,
          DEFAULT_PDF_COLORS.bodyText
        )
      );

      setColorModalOpen(reportType);
    },
    [pdfThemeColor, pdfTextColor]
  );

  const handleGenerateAdmission = async () => {
    const rows = await fetchAdmissionReport();
    if (rows?.length) {
      openPdfColorPicker("admission");
    }
  };

  const handleGenerateCourse = async () => {
    const rows = await fetchCourseReport();
    if (rows?.length) {
      openPdfColorPicker("course");
    }
  };

  const handleGenerateRegistration = async () => {
    const rows = await fetchRegistrationReport();
    if (rows?.length) {
      openPdfColorPicker("registration");
    }
  };

  const handleGenerateCancellation = () => {
    setError("");
    const rows = applyCancellationFilters();
    setCancellationRows(rows);

    if (rows?.length) {
      openPdfColorPicker("cancellation");
    }
  };

  /* ------------------------------------------------------------------------ */
  /* GLOBAL BRANCH LABEL                                                      */
  /* ------------------------------------------------------------------------ */

  const admissionBranchLabel =
    globalBranchName;

  const courseBranchLabel =
    globalBranchName;

  const cancellationBranchLabel =
    globalBranchName;

  const registrationBranchLabel =
    globalBranchName;


  /* ------------------------------------------------------------------------ */
  /* PROFESSIONAL FRONTEND PDF GENERATOR                                      */
  /* ------------------------------------------------------------------------ */

  const createProfessionalReportPdf =
    useCallback(
      ({
        title,
        rows,
        themeColor,
        textColor,
        branchLabel,
        filters,
        summaryEntries,
        head,
        body,
      }) => {
        const doc = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: "a4",
        });

        const pageWidth =
          doc.internal.pageSize.getWidth();

        const pageHeight =
          doc.internal.pageSize.getHeight();

        const margin = 10;

        const safeThemeColor =
          normalizeHex(
            themeColor,
            DEFAULT_PDF_COLORS.primary
          );

        const safeTextColor =
          normalizeHex(
            textColor,
            DEFAULT_PDF_COLORS.bodyText
          );

        const themeRgb =
          hexToRgb(safeThemeColor);

        const textRgb =
          hexToRgb(safeTextColor);

        const neutralRgb =
          hexToRgb(
            DEFAULT_PDF_COLORS.alternateRow
          );

        const borderRgb =
          hexToRgb(
            DEFAULT_PDF_COLORS.border
          );

        const mutedRgb =
          hexToRgb(
            DEFAULT_PDF_COLORS.mutedText
          );

        const headerTextRgb =
          hexToRgb(
            DEFAULT_PDF_COLORS.headerText
          );

        const drawHeader = (
          compact = false
        ) => {
          const headerHeight =
            compact ? 28 : 40;

          doc.setFillColor(
            ...themeRgb
          );

          doc.rect(
            0,
            0,
            pageWidth,
            headerHeight,
            "F"
          );

          if (compact) {
            if (branchLogo) {
              try {
                doc.addImage(
                  branchLogo,
                  "PNG",
                  margin,
                  5,
                  18,
                  18
                );
              } catch (logoError) {
                console.warn(
                  "Unable to add branch logo:",
                  logoError
                );
              }
            }

            doc.setTextColor(
              ...headerTextRgb
            );

            doc.setFont(
              "helvetica",
              "bold"
            );

            doc.setFontSize(11);

            doc.text(
              organizationName ||
                "Organization",
              pageWidth / 2,
              10,
              {
                align: "center",
              }
            );

            doc.setFont(
              "helvetica",
              "normal"
            );

            doc.setFontSize(8);

            doc.text(
              `${branchLabel || "Branch"} • ${title}`,
              pageWidth / 2,
              17,
              {
                align: "center",
              }
            );

            doc.setFontSize(7);

            doc.text(
              `Generated: ${new Date().toLocaleString(
                "en-GB",
                {
                  dateStyle: "short",
                  timeStyle: "short",
                }
              )}`,
              pageWidth - margin,
              24,
              {
                align: "right",
              }
            );

            return;
          }

          /* Logo */
          doc.setFillColor(
            ...headerTextRgb
          );

          doc.roundedRect(
            margin,
            7,
            26,
            26,
            3,
            3,
            "F"
          );

          if (branchLogo) {
            try {
              doc.addImage(
                branchLogo,
                "PNG",
                margin + 2,
                9,
                22,
                22
              );
            } catch (logoError) {
              console.warn(
                "Unable to add branch logo:",
                logoError
              );
            }
          }

          /* Center organization / branch / title */
          doc.setTextColor(
              ...headerTextRgb
            );

          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.setFontSize(16);

          const organizationLines =
            doc.splitTextToSize(
              organizationName ||
                "Organization",
              105
            );

          doc.text(
            organizationLines,
            pageWidth / 2,
            12,
            {
              align: "center",
              baseline: "top",
            }
          );

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(9.5);

          doc.text(
            branchLabel || "Branch",
            pageWidth / 2,
            22,
            {
              align: "center",
            }
          );

          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.setFontSize(11.5);

          doc.text(
            title,
            pageWidth / 2,
            29,
            {
              align: "center",
            }
          );

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(7.5);

          doc.text(
            `Generated: ${new Date().toLocaleString(
              "en-GB",
              {
                dateStyle: "short",
                timeStyle: "short",
              }
            )}`,
            pageWidth - margin,
            24,
            {
              align: "right",
            }
          );
        };

        drawHeader(false);

        let y = 48;

        /* Filters section */
        doc.setTextColor(
          ...textRgb
        );

        doc.setFont(
          "helvetica",
          "bold"
        );

        doc.setFontSize(9);

        doc.text(
          "FILTERS",
          margin,
          y
        );

        y += 3;

        const filterEntries =
          Object.entries(
            filters || {}
          ).filter(
            ([, value]) =>
              value !== null &&
              value !== undefined &&
              value !== ""
          );

        const filterText =
          filterEntries.length
            ? filterEntries
                .map(
                  ([key, value]) =>
                    `${labelize(key)}: ${formatValue(
                      value
                    )}`
                )
                .join("   |   ")
            : "No filters applied";

        const filterLines =
          doc.splitTextToSize(
            filterText,
            pageWidth -
              margin * 2 -
              8
          );

        const filterBoxHeight =
          Math.max(
            10,
            filterLines.length * 3.8 +
              7
          );

        doc.setFillColor(
          ...neutralRgb
        );

        doc.setDrawColor(
          ...borderRgb
        );

        doc.roundedRect(
          margin,
          y,
          pageWidth - margin * 2,
          filterBoxHeight,
          2,
          2,
          "FD"
        );

        doc.setTextColor(
          ...textRgb
        );

        doc.setFont(
          "helvetica",
          "normal"
        );

        doc.setFontSize(7.5);

        doc.text(
          filterLines,
          margin + 4,
          y + 6
        );

        y += filterBoxHeight + 7;

        /* Summary section */
        const validSummary =
          (summaryEntries || []).filter(
            ([, value]) =>
              value !== null &&
              value !== undefined &&
              value !== ""
          );

        if (validSummary.length) {
          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.setFontSize(9);

          doc.text(
            "SUMMARY",
            margin,
            y
          );

          y += 3;

          const summaryValues =
            validSummary.map(
              ([label, value]) =>
                `${label}: ${formatValue(
                  value
                )}`
            );

          const summaryText =
            summaryValues.join(
              "   |   "
            );

          const summaryLines =
            doc.splitTextToSize(
              summaryText,
              pageWidth -
                margin * 2 -
                8
            );

          const summaryBoxHeight =
            Math.max(
              10,
              summaryLines.length * 3.8 +
                7
            );

          doc.setFillColor(
            248,
            250,
            252
          );

          doc.setDrawColor(
            ...borderRgb
          );

          doc.roundedRect(
            margin,
            y,
            pageWidth - margin * 2,
            summaryBoxHeight,
            2,
            2,
            "FD"
          );

          doc.setTextColor(
            ...textRgb
          );

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(7.5);

          doc.text(
            summaryLines,
            margin + 4,
            y + 6
          );

          y +=
            summaryBoxHeight +
            7;
        }

        const tableHead =
          [head];

        const tableBody =
          body && body.length
            ? body
            : [[
                "No records found",
              ]];

        autoTable(doc, {
          startY: y,
          head: tableHead,
          body: tableBody,
          theme: "grid",
          margin: {
            top: 47,
            left: margin,
            right: margin,
            bottom: 13,
          },
          styles: {
            font: "helvetica",
            fontSize:
              head.length >= 9
                ? 7.2
                : 8.2,
            textColor:
              textRgb,
            cellPadding:
              head.length >= 9
                ? 1.8
                : 2.1,
            valign: "middle",
            overflow: "linebreak",
            lineColor:
              borderRgb,
            lineWidth:
              0.18,
          },
          headStyles: {
            fillColor:
              themeRgb,
            textColor:
              headerTextRgb,
            fontStyle: "bold",
            fontSize:
              head.length >= 9
                ? 7.3
                : 8.2,
            halign: "center",
            valign: "middle",
            cellPadding: 2.1,
          },
          bodyStyles: {
            textColor: textRgb,
          },
          alternateRowStyles: {
            fillColor:
              neutralRgb,
          },
          didParseCell: (
            hookData
          ) => {
            if (
              hookData.section ===
              "body"
            ) {
              hookData.cell.styles.lineColor =
                borderRgb;
            }
          },
          willDrawPage: (
            hookData
          ) => {
            if (
              hookData.pageNumber >
              1
            ) {
              drawHeader(true);
            }
          },
          didDrawPage: (
            hookData
          ) => {
            const pageNo =
              hookData.pageNumber;

            const pageCount =
              doc.internal.getNumberOfPages();

            doc.setDrawColor(
              ...borderRgb
            );

            doc.setLineWidth(
              0.2
            );

            doc.line(
              margin,
              pageHeight - 11,
              pageWidth - margin,
              pageHeight - 11
            );

            doc.setTextColor(
              ...mutedRgb
            );

            doc.setFont(
              "helvetica",
              "normal"
            );

            doc.setFontSize(6.8);

            doc.text(
              `${organizationName || "Organization"} • ${title}`,
              margin,
              pageHeight - 6
            );

            doc.text(
              `Page ${pageNo} of ${pageCount}`,
              pageWidth - margin,
              pageHeight - 6,
              {
                align: "right",
              }
            );
          },
        });

        return doc;
      },
      [
        branchLogo,
        organizationName,
      ]
    );

  /* ------------------------------------------------------------------------ */
  /* REPORT-SPECIFIC PDF BUILDERS                                             */
  /* ------------------------------------------------------------------------ */

  const createAdmissionPdf =
    useCallback(
      (
        rows,
        themeColor,
        textColor
      ) =>
        createProfessionalReportPdf({
          title: "Admission Report",
          rows,
          themeColor,
          textColor,
          branchLabel:
            admissionBranchLabel,
          filters: {
            Branch:
              admissionBranchLabel,
            From:
              formatDate(
                admissionFilters.fromDate
              ),
            To:
              formatDate(
                admissionFilters.toDate
              ),
            ...(admissionFilters
              .selectedAdmissionNo
              ? {
                  "Admission No":
                    admissionFilters.selectedAdmissionNo,
                }
              : {}),
          },
          summaryEntries: [
            [
              "Total Admissions",
              rows.length,
            ],
            [
              "Male",
              rows.filter(
                (row) =>
                  String(
                    row?.gender ||
                      ""
                  ).toLowerCase() ===
                  "male"
              ).length,
            ],
            [
              "Female",
              rows.filter(
                (row) =>
                  String(
                    row?.gender ||
                      ""
                  ).toLowerCase() ===
                  "female"
              ).length,
            ],
          ],
          head: [
            "S.No.",
            "Admission Code",
            "Student Name",
            "Email",
            "Gender",
            "Courses",
            "Mobile No.",
            "Admission Date",
            "Status",
          ],
          body: rows.map(
            (row, index) => [
              index + 1,
              formatValue(
                row?.admission_code
              ),
              formatValue(
                row?.student_name
              ),
              formatValue(
                row?.email
              ),
              formatValue(
                row?.gender
              ),
              formatValue(
                row?.courses
              ),
              formatValue(
                row?.mobile_no
              ),
              formatDate(
                row?.admission_date
              ),
              formatValue(
                row?.status
              ),
            ]
          ),
        }),
      [
        admissionBranchLabel,
        admissionFilters,
        createProfessionalReportPdf,
      ]
    );

  const createCoursePdf =
    useCallback(
      (
        rows,
        themeColor,
        textColor
      ) =>
        createProfessionalReportPdf({
          title:
            "Admission (Course) Report",
          rows,
          themeColor,
          textColor,
          branchLabel:
            courseBranchLabel,
          filters: {
            Branch:
              courseBranchLabel,
            From:
              formatDate(
                courseFilters.fromDate
              ),
            To:
              formatDate(
                courseFilters.toDate
              ),
            ...(courseFilters
              .selectedCourse
              ? {
                  Course:
                    courseFilters.selectedCourse,
                }
              : {}),
          },
          summaryEntries: [
            [
              "Total Courses",
              rows.length,
            ],
            [
              "Male",
              rows.reduce(
                (
                  total,
                  row
                ) =>
                  total +
                  Number(
                    row?.male || 0
                  ),
                0
              ),
            ],
            [
              "Female",
              rows.reduce(
                (
                  total,
                  row
                ) =>
                  total +
                  Number(
                    row?.female || 0
                  ),
                0
              ),
            ],
            [
              "Total Students",
              rows.reduce(
                (
                  total,
                  row
                ) =>
                  total +
                  Number(
                    row?.total || 0
                  ),
                0
              ),
            ],
          ],
          head: [
            "S.No.",
            "Course Code",
            "Course Name",
            "Male",
            "Female",
            "Total",
          ],
          body: rows.map(
            (row, index) => [
              index + 1,
              formatValue(
                row?.course_code
              ),
              formatValue(
                row?.course_name
              ),
              formatValue(
                row?.male
              ),
              formatValue(
                row?.female
              ),
              formatValue(
                row?.total
              ),
            ]
          ),
        }),
      [
        courseBranchLabel,
        courseFilters,
        createProfessionalReportPdf,
      ]
    );

  const createCancellationPdf =
    useCallback(
      (
        rows,
        themeColor,
        textColor
      ) =>
        createProfessionalReportPdf({
          title:
            "Admission Cancellation Report",
          rows,
          themeColor,
          textColor,
          branchLabel:
            cancellationBranchLabel,
          filters: {
            Branch:
              cancellationBranchLabel,
            From:
              formatDate(
                cancellationFilters.fromDate
              ),
            To:
              formatDate(
                cancellationFilters.toDate
              ),
            ...(cancellationFilters
              .selectedAdmissionNo
              ? {
                  "Admission No":
                    cancellationFilters.selectedAdmissionNo,
                }
              : {}),
          },
          summaryEntries: [
            [
              "Total Cancellations",
              rows.length,
            ],
          ],
          head: [
            "S.No.",
            "Admission No",
            "Cancellation Date",
            "Reason",
          ],
          body: rows.map(
            (row, index) => [
              index + 1,
              formatValue(
                row?.admissionNo
              ),
              formatDate(
                row?.cancellationDate
              ),
              formatValue(
                row?.reason
              ),
            ]
          ),
        }),
      [
        cancellationBranchLabel,
        cancellationFilters,
        createProfessionalReportPdf,
      ]
    );

  const createRegistrationPdf =
    useCallback(
      (
        rows,
        themeColor,
        textColor
      ) =>
        createProfessionalReportPdf({
          title:
            "Admission Registration Report",
          rows,
          themeColor,
          textColor,
          branchLabel:
            registrationBranchLabel,
          filters: {
            Branch:
              registrationBranchLabel,
            From:
              formatDate(
                registrationFilters.fromDate
              ),
            To:
              formatDate(
                registrationFilters.toDate
              ),
            ...(registrationFilters
              .selectedRegistrationNo
              ? {
                  "Registration No":
                    registrationFilters.selectedRegistrationNo,
                }
              : {}),
          },
          summaryEntries: [
            [
              "Total Registrations",
              rows.length,
            ],
            [
              "Converted to Admission",
              rows.filter(
                (row) =>
                  String(
                    row?.is_admitted ||
                      ""
                  ).toLowerCase() ===
                  "yes"
              ).length,
            ],
          ],
          head: [
            "S.No.",
            "Registration Code",
            "Candidate Name",
            "Mobile No.",
            "Email",
            "Gender",
            "Registration Date",
            "Organization",
            "Branch",
            "Courses",
            "Admitted",
          ],
          body: rows.map(
            (row, index) => [
              index + 1,
              formatValue(
                row?.registration_code
              ),
              formatValue(
                row?.candidate_name
              ),
              formatValue(
                row?.mobile_no
              ),
              formatValue(
                row?.email
              ),
              formatValue(
                row?.gender
              ),
              formatDate(
                row?.registration_date
              ),
              formatValue(
                row?.organization
              ),
              formatValue(
                row?.branch
              ),
              formatValue(
                row?.courses
              ),
              formatValue(
                row?.is_admitted
              ),
            ]
          ),
        }),
      [
        registrationBranchLabel,
        registrationFilters,
        createProfessionalReportPdf,
      ]
    );

  /* ------------------------------------------------------------------------ */
  /* GENERATE PDF                                                             */
  /* ------------------------------------------------------------------------ */

  const generatePdfForReport =
    async (
      reportType,
      themeColor,
      textColor
    ) => {
      setPdfLoading(true);
      setError("");

      try {
        const safeThemeColor = normalizeHex(
          themeColor,
          "#1E293B"
        );
        const safeTextColor = normalizeHex(
          textColor,
          "#0F172A"
        );

        updatePdfThemeColor?.(safeThemeColor);
        updatePdfTextColor?.(safeTextColor);

        let rows = [];
        let doc = null;
        let filename = "Report.pdf";

        if (reportType === "admission") {
          rows = admissionRows;
          filename = `Admission_Report_${admissionFilters.fromDate}_to_${admissionFilters.toDate}.pdf`;
          if (!rows.length) {
            notifyError(
              "No admission records found. Please generate the report first."
            );
            return;
          }
          doc = createAdmissionPdf(
            rows,
            safeThemeColor,
            safeTextColor
          );
        } else if (reportType === "course") {
          rows = courseRows;
          filename = `Course_Wise_Admission_Report_${courseFilters.fromDate}_to_${courseFilters.toDate}.pdf`;
          if (!rows.length) {
            notifyError(
              "No course-wise admission records found. Please generate the report first."
            );
            return;
          }
          doc = createCoursePdf(
            rows,
            safeThemeColor,
            safeTextColor
          );
        } else if (reportType === "registration") {
          rows = registrationRows;
          filename = `Admission_Registration_Report_${registrationFilters.fromDate}_to_${registrationFilters.toDate}.pdf`;
          if (!rows.length) {
            notifyError(
              "No registration records found. Please generate the report first."
            );
            return;
          }
          doc = createRegistrationPdf(
            rows,
            safeThemeColor,
            safeTextColor
          );
        } else if (reportType === "cancellation") {
          rows = cancellationRows;
          filename = `Admission_Cancellation_Report_${cancellationFilters.fromDate}_to_${cancellationFilters.toDate}.pdf`;
          if (!rows.length) {
            notifyError(
              "No cancellation records found. Please generate the report first."
            );
            return;
          }
          doc = createCancellationPdf(
            rows,
            safeThemeColor,
            safeTextColor
          );
        } else {
          notifyError("Unknown report type.");
          return;
        }

        if (!doc) {
          notifyError("Unable to create PDF.");
          return;
        }

        // IMPORTANT: PDF is generated from the already loaded report rows.
        // No report API and no /export/pdf/ API is called here.
        doc.save(filename);
        setColorModalOpen(null);
      } catch (generateError) {
        console.error(
          "PDF generation error:",
          generateError
        );

        notifyError(
          errorMessage(generateError)
        );
      } finally {
        setPdfLoading(false);
      }
    };

  /* ------------------------------------------------------------------------ */
  /* EXCEL - ADMISSION                                                        */
  /* ------------------------------------------------------------------------ */

  const exportAdmissionExcel =
    async () => {
      setDownloadLoading(
        true
      );
      setError("");

      try {
        const response =
          await API.post(
            "/api/reports/admission/report/export/excel/",
            admissionPayload,
            {
              responseType:
                "blob",
            }
          );

        const blob =
          new Blob(
            [response.data],
            {
              type:
                response
                  .headers?.[
                  "content-type"
                ] ||
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }
          );

        const url =
          window.URL.createObjectURL(
            blob
          );

        const link =
          document.createElement(
            "a"
          );

        link.href = url;

        link.download = `Admission_Report_${admissionFilters.fromDate}_to_${admissionFilters.toDate}.xlsx`;

        document.body.appendChild(
          link
        );

        link.click();

        link.remove();

        window.URL.revokeObjectURL(
          url
        );
      } catch (exportError) {
        console.error(
          "Admission Excel export error:",
          exportError
        );

        notifyError(
          errorMessage(
            exportError
          )
        );
      } finally {
        setDownloadLoading(
          false
        );
      }
    };

  /* ------------------------------------------------------------------------ */
  /* EXCEL - COURSE                                                           */
  /* ------------------------------------------------------------------------ */

  const exportCourseExcel =
    async () => {
      setDownloadLoading(
        true
      );
      setError("");

      try {
        const response =
          await API.post(
            "/api/reports/reports/course-wise-admission/export/excel/",
            coursePayload,
            {
              responseType:
                "blob",
            }
          );

        const blob =
          new Blob(
            [response.data],
            {
              type:
                response
                  .headers?.[
                  "content-type"
                ] ||
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }
          );

        const url =
          window.URL.createObjectURL(
            blob
          );

        const link =
          document.createElement(
            "a"
          );

        link.href = url;

        link.download = `Course_Wise_Admission_${courseFilters.fromDate}_to_${courseFilters.toDate}.xlsx`;

        document.body.appendChild(
          link
        );

        link.click();

        link.remove();

        window.URL.revokeObjectURL(
          url
        );
      } catch (exportError) {
        console.error(
          "Course Excel export error:",
          exportError
        );

        notifyError(
          errorMessage(
            exportError
          )
        );
      } finally {
        setDownloadLoading(
          false
        );
      }
    };

  /* ------------------------------------------------------------------------ */
  /* EXCEL - REGISTRATION                                                     */
  /* ------------------------------------------------------------------------ */

  const exportRegistrationExcel =
    async () => {
      setDownloadLoading(
        true
      );
      setError("");

      try {
        const response =
          await API.post(
            "/api/reports/registration-report/export/excel/",
            registrationPayload,
            {
              responseType:
                "blob",
            }
          );

        const blob =
          new Blob(
            [response.data],
            {
              type:
                response
                  .headers?.[
                  "content-type"
                ] ||
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            }
          );

        const url =
          window.URL.createObjectURL(
            blob
          );

        const link =
          document.createElement(
            "a"
          );

        link.href = url;

        link.download = `Registration_Report_${registrationFilters.fromDate}_to_${registrationFilters.toDate}.xlsx`;

        document.body.appendChild(
          link
        );

        link.click();

        link.remove();

        window.URL.revokeObjectURL(
          url
        );
      } catch (exportError) {
        console.error(
          "Registration Excel export error:",
          exportError
        );

        notifyError(
          errorMessage(
            exportError
          )
        );
      } finally {
        setDownloadLoading(
          false
        );
      }
    };

  /* ------------------------------------------------------------------------ */
  /* EXCEL - CANCELLATION                                                     */
  /* ------------------------------------------------------------------------ */

  const exportCancellationExcel =
    () => {
      try {
        const excelRows =
          cancellationRows.map(
            (
              row,
              index
            ) => ({
              "S.No.":
                index + 1,

              "Admission No":
                formatValue(
                  row.admissionNo
                ),

              "Cancellation Date":
                formatDate(
                  row.cancellationDate
                ),

              Reason:
                formatValue(
                  row.reason
                ),
            })
          );

        const worksheet =
          XLSX.utils.json_to_sheet(
            excelRows
          );

        worksheet["!cols"] = [
          { wch: 8 },
          { wch: 22 },
          { wch: 22 },
          { wch: 40 },
        ];

        const workbook =
          XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          "Cancellation Report"
        );

        XLSX.writeFile(
          workbook,
          `Admission_Cancellation_Report_${cancellationFilters.fromDate}_to_${cancellationFilters.toDate}.xlsx`
        );
      } catch (exportError) {
        console.error(
          "Cancellation Excel export error:",
          exportError
        );

        notifyError(
          errorMessage(
            exportError
          )
        );
      }
    };

  /* ------------------------------------------------------------------------ */
  /* REFRESH ALL                                                              */
  /* ------------------------------------------------------------------------ */

  const refreshAll =
    async () => {
      setError("");

      await Promise.all([
        fetchAdmissionReport(),
        fetchCourseReport(),
        fetchRegistrationReport(),
      ]);
    };

  /* ------------------------------------------------------------------------ */
  /* APPLY FILTERS                                                            */
  /* ------------------------------------------------------------------------ */

  const applyAdmissionFilters =
    async () => {
      setAdmissionFilterOpen(
        false
      );

      await fetchAdmissionReport();
    };

  const applyCourseFilters =
    async () => {
      setCourseFilterOpen(
        false
      );

      await fetchCourseReport();
    };

  const applyRegistrationFilters =
    async () => {
      setRegistrationFilterOpen(
        false
      );

      await fetchRegistrationReport();
    };

  const applyCancellationFilters =
    () => {
      setCancellationFilterOpen(
        false
      );

      let rows =
        initialCancellationRows;

      if (
        cancellationFilters.selectedAdmissionNo
      ) {
        rows =
          rows.filter(
            (row) =>
              String(
                row.admissionNo
              ) ===
              String(
                cancellationFilters.selectedAdmissionNo
              )
          );
      }

      setCancellationRows(
        rows
      );

      return rows;
    };

  /* ------------------------------------------------------------------------ */
  /* AUTO FETCH INITIAL REPORT / GLOBAL BRANCH                                */
  /* ------------------------------------------------------------------------ */

  const reportFetchersRef = useRef({
    admission: fetchAdmissionReport,
    course: fetchCourseReport,
    registration: fetchRegistrationReport,
  });

  reportFetchersRef.current = {
    admission: fetchAdmissionReport,
    course: fetchCourseReport,
    registration: fetchRegistrationReport,
  };

  useEffect(() => {
    if (
      !effectiveOrganizationId ||
      !globalBranchId
    ) {
      return;
    }

    const fetcher =
      reportFetchersRef.current[reportTab];

    if (fetcher) {
      fetcher();
    }
  }, [
    effectiveOrganizationId,
    globalBranchId,
    reportTab,
  ]);

  /* ------------------------------------------------------------------------ */
  /* UI                                                                       */
  /* ------------------------------------------------------------------------ */

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
   
{/* REPORT TABS */}
<Box
  sx={{
    mb: 2,
    p: 0.5,
    borderRadius: 2,
    borderBottom: "1px solid",
    borderColor: "divider",
    overflowX: "auto",
  }}
>
  <Tabs
    value={reportTab}
    onChange={(_, value) => {
      setReportTab(value);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }}
    variant="scrollable"
    scrollButtons="auto"
    allowScrollButtonsMobile
    sx={{
      minHeight: 42,

      "& .MuiTabs-flexContainer": {
        gap: 0.5,
      },

      "& .MuiTabs-indicator": {
        display: "none",
      },

      "& .MuiTab-root": {
        minHeight: 42,
        minWidth: "auto",

        px: {
          xs: 1.5,
          sm: 2.2,
        },

        borderRadius: 1.5,

        color: "text.primary",

        fontWeight: 600,
        textTransform: "none",

        fontSize: {
          xs: 13,
          sm: 14,
        },

        opacity: 1,

        whiteSpace: "nowrap",

        transition: "all 0.2s ease",

        "&:hover": {
          backgroundColor: "action.hover",
          color: "text.primary",
        },
      },

      "& .MuiTab-root.Mui-selected": {
        backgroundColor: "primary.main",
        color: "primary.contrastText",

        "&:hover": {
          backgroundColor: "primary.dark",
        },
      },

      "& .MuiTabs-scrollButtons": {
        color: "text.primary",

        "&.Mui-disabled": {
          opacity: 0.3,
        },
      },
    }}
  >
    <Tab
      value="admission"
      label="Admission Report"
    />

    <Tab
      value="course"
      label="Admission (Course) Report"
    />

    <Tab
      value="cancellation"
      label="Admission Cancellation Report"
    />

    <Tab
      value="registration"
      label="Admission Registration Report"
    />
  </Tabs>
</Box>




      {error && (
        <Alert severity="error" variant="outlined" sx={{ mt: 2, borderRadius: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* REPORT TOOLBAR */}
      {(() => {
        const view = {
          admission: {
            rows: admissionRows,
            loading: admissionLoading,
            filter: () => setAdmissionFilterOpen(true),
            generate: handleGenerateAdmission,
            excel: exportAdmissionExcel,
            reset: resetAdmissionFilters,
          },
          course: {
            rows: courseRows,
            loading: courseLoading,
            filter: () => setCourseFilterOpen(true),
            generate: handleGenerateCourse,
            excel: exportCourseExcel,
            reset: resetCourseFilters,
          },
          cancellation: {
            rows: cancellationRows,
            loading: false,
            filter: () => setCancellationFilterOpen(true),
            generate: handleGenerateCancellation,
            excel: exportCancellationExcel,
            reset: resetCancellationFilters,
          },
          registration: {
            rows: registrationRows,
            loading: registrationLoading,
            filter: () => setRegistrationFilterOpen(true),
            generate: handleGenerateRegistration,
            excel: exportRegistrationExcel,
            reset: resetRegistrationFilters,
          },
        }[reportTab];

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "flex-end",
              gap: 1,
              mt: 2,
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              startIcon={<FilterAltOutlined />}
              onClick={view.filter}
              sx={{ textTransform: "none", borderRadius: 2, px: 2, fontWeight: 600 }}
            >
              Filters
            </Button>

            <Button
              variant="contained"
              startIcon={view.loading ? <CircularProgress size={18} color="inherit" /> : <AssessmentOutlined />}
              onClick={view.generate}
              disabled={view.loading}
              sx={{ textTransform: "none", borderRadius: 2, px: 2, fontWeight: 600 }}
            >
              {view.loading ? "Generating..." : "Generate Report"}
            </Button>

            <Button
              variant="contained"
              color="success"
              startIcon={
                downloadLoading ? (
                  <CircularProgress
                    size={18}
                    color="inherit"
                  />
                ) : (
                  <DownloadOutlined />
                )
              }
              onClick={view.excel}
              disabled={
                !view.rows.length ||
                downloadLoading
              }
              sx={{ textTransform: "none", borderRadius: 2, px: 2, fontWeight: 600 }}
            >
              {downloadLoading
                ? "Exporting..."
                : "Export Excel"}
            </Button>
          </Box>
        );
      })()}

      {/* REPORT TABLE */}
      {reportTab === "admission" && (
        <ReportTable rows={admissionRows} columns={getAdmissionColumns()} loading={admissionLoading} hasLoaded={admissionHasLoaded} />
      )}

      {reportTab === "course" && (
        <ReportTable rows={courseRows} columns={getCourseColumns()} loading={courseLoading} hasLoaded={courseHasLoaded} />
      )}

      {reportTab === "cancellation" && (
        <ReportTable rows={cancellationRows} columns={getCancellationColumns()} loading={false} />
      )}

      {reportTab === "registration" && (
        <ReportTable rows={registrationRows} columns={getRegistrationColumns()} loading={registrationLoading} hasLoaded={registrationHasLoaded} />
      )}

      {/* ADMISSION FILTER MODAL                                            */}
      {/* ------------------------------------------------------------------ */}

      <FilterModal
        open={
          admissionFilterOpen
        }
        onClose={() =>
          setAdmissionFilterOpen(
            false
          )
        }
        title="Admission Report"
        globalBranchName={globalBranchName}
        admissionType={
          admissionFilters.admissionType
        }
        onAdmissionTypeChange={(
          value
        ) =>
          setAdmissionFilters(
            (prev) => ({
              ...prev,
              admissionType:
                value,
              selectedAdmissionNo:
                "",
            })
          )
        }
        selectedAdmissionNo={
          admissionFilters.selectedAdmissionNo
        }
        onSelectedAdmissionNoChange={(
          value
        ) =>
          setAdmissionFilters(
            (prev) => ({
              ...prev,
              selectedAdmissionNo:
                value,
            })
          )
        }
        admissionOptions={
          admissionOptions
        }
        loadingAdmissions={
          loadingAdmissions
        }
        courseType="allCourse"
        onCourseTypeChange={() => {}}
        selectedCourse=""
        onSelectedCourseChange={() => {}}
        courseOptions={[]}
        loadingCourses={false}
        registrationType="allRegistration"
        onRegistrationTypeChange={() => {}}
        selectedRegistrationNo=""
        onSelectedRegistrationNoChange={() => {}}
        registrationOptions={[]}
        loadingRegistrations={
          false
        }
        fromDate={
          admissionFilters.fromDate
        }
        toDate={
          admissionFilters.toDate
        }
        onFromDateChange={(
          value
        ) =>
          setAdmissionFilters(
            (prev) => ({
              ...prev,
              fromDate: value,
            })
          )
        }
        onToDateChange={(
          value
        ) =>
          setAdmissionFilters(
            (prev) => ({
              ...prev,
              toDate: value,
            })
          )
        }
        showCourseFilter={
          false
        }
        showAdmissionFilter={
          true
        }
        showRegistrationFilter={
          false
        }
        cancellationType=""
        onCancellationTypeChange={() => {}}
        onReset={
          resetAdmissionFilters
        }
        onApply={
          applyAdmissionFilters
        }
      />

      {/* ------------------------------------------------------------------ */}
      {/* COURSE FILTER MODAL                                                */}
      {/* ------------------------------------------------------------------ */}

      <FilterModal
        open={
          courseFilterOpen
        }
        onClose={() =>
          setCourseFilterOpen(
            false
          )
        }
        title="Admission (Course) Report"
        globalBranchName={globalBranchName}
        admissionType={
          courseFilters.admissionType
        }
        onAdmissionTypeChange={(
          value
        ) =>
          setCourseFilters(
            (prev) => ({
              ...prev,
              admissionType:
                value,
              selectedAdmissionNo:
                "",
            })
          )
        }
        selectedAdmissionNo={
          courseFilters.selectedAdmissionNo
        }
        onSelectedAdmissionNoChange={(
          value
        ) =>
          setCourseFilters(
            (prev) => ({
              ...prev,
              selectedAdmissionNo:
                value,
            })
          )
        }
        admissionOptions={
          admissionOptions
        }
        loadingAdmissions={
          loadingAdmissions
        }
        courseType={
          courseFilters.courseType
        }
        onCourseTypeChange={(
          value
        ) =>
          setCourseFilters(
            (prev) => ({
              ...prev,
              courseType:
                value,
              selectedCourse:
                "",
            })
          )
        }
        selectedCourse={
          courseFilters.selectedCourse
        }
        onSelectedCourseChange={(
          value
        ) =>
          setCourseFilters(
            (prev) => ({
              ...prev,
              selectedCourse:
                value,
            })
          )
        }
        courseOptions={
          courseOptions
        }
        loadingCourses={
          loadingCourses
        }
        registrationType="allRegistration"
        onRegistrationTypeChange={() => {}}
        selectedRegistrationNo=""
        onSelectedRegistrationNoChange={() => {}}
        registrationOptions={[]}
        loadingRegistrations={
          false
        }
        fromDate={
          courseFilters.fromDate
        }
        toDate={
          courseFilters.toDate
        }
        onFromDateChange={(
          value
        ) =>
          setCourseFilters(
            (prev) => ({
              ...prev,
              fromDate: value,
            })
          )
        }
        onToDateChange={(
          value
        ) =>
          setCourseFilters(
            (prev) => ({
              ...prev,
              toDate: value,
            })
          )
        }
        showCourseFilter={
          true
        }
        showAdmissionFilter={
          true
        }
        showRegistrationFilter={
          false
        }
        cancellationType=""
        onCancellationTypeChange={() => {}}
        onReset={
          resetCourseFilters
        }
        onApply={
          applyCourseFilters
        }
      />

      {/* ------------------------------------------------------------------ */}
      {/* CANCELLATION FILTER MODAL                                          */}
      {/* ------------------------------------------------------------------ */}

      <FilterModal
        open={
          cancellationFilterOpen
        }
        onClose={() =>
          setCancellationFilterOpen(
            false
          )
        }
        title="Admission Cancellation Report"
        globalBranchName={globalBranchName}
        admissionType="allAdmission"
        onAdmissionTypeChange={() => {}}
        selectedAdmissionNo={
          cancellationFilters.selectedAdmissionNo
        }
        onSelectedAdmissionNoChange={(
          value
        ) =>
          setCancellationFilters(
            (prev) => ({
              ...prev,
              selectedAdmissionNo:
                value,
            })
          )
        }
        admissionOptions={
          admissionOptions
        }
        loadingAdmissions={
          loadingAdmissions
        }
        courseType="allCourse"
        onCourseTypeChange={() => {}}
        selectedCourse=""
        onSelectedCourseChange={() => {}}
        courseOptions={[]}
        loadingCourses={false}
        registrationType="allRegistration"
        onRegistrationTypeChange={() => {}}
        selectedRegistrationNo=""
        onSelectedRegistrationNoChange={() => {}}
        registrationOptions={[]}
        loadingRegistrations={
          false
        }
        fromDate={
          cancellationFilters.fromDate
        }
        toDate={
          cancellationFilters.toDate
        }
        onFromDateChange={(
          value
        ) =>
          setCancellationFilters(
            (prev) => ({
              ...prev,
              fromDate: value,
            })
          )
        }
        onToDateChange={(
          value
        ) =>
          setCancellationFilters(
            (prev) => ({
              ...prev,
              toDate: value,
            })
          )
        }
        showCourseFilter={
          false
        }
        showAdmissionFilter={
          false
        }
        showRegistrationFilter={
          false
        }
        cancellationType={
          cancellationFilters.cancellationType
        }
        onCancellationTypeChange={(
          value
        ) =>
          setCancellationFilters(
            (prev) => ({
              ...prev,
              cancellationType:
                value,
            })
          )
        }
        onReset={
          resetCancellationFilters
        }
        onApply={
          applyCancellationFilters
        }
      />

      {/* ------------------------------------------------------------------ */}
      {/* REGISTRATION FILTER MODAL                                          */}
      {/* ------------------------------------------------------------------ */}

      <FilterModal
        open={
          registrationFilterOpen
        }
        onClose={() =>
          setRegistrationFilterOpen(
            false
          )
        }
        title="Admission Registration Report"
        globalBranchName={globalBranchName}
        admissionType="allAdmission"
        onAdmissionTypeChange={() => {}}
        selectedAdmissionNo=""
        onSelectedAdmissionNoChange={() => {}}
        admissionOptions={[]}
        loadingAdmissions={false}
        courseType="allCourse"
        onCourseTypeChange={() => {}}
        selectedCourse=""
        onSelectedCourseChange={() => {}}
        courseOptions={[]}
        loadingCourses={false}
        registrationType={
          registrationFilters.registrationType
        }
        onRegistrationTypeChange={(
          value
        ) =>
          setRegistrationFilters(
            (prev) => ({
              ...prev,
              registrationType:
                value,
              selectedRegistrationNo:
                "",
            })
          )
        }
        selectedRegistrationNo={
          registrationFilters.selectedRegistrationNo
        }
        onSelectedRegistrationNoChange={(
          value
        ) =>
          setRegistrationFilters(
            (prev) => ({
              ...prev,
              selectedRegistrationNo:
                value,
            })
          )
        }
        registrationOptions={
          registrationOptions
        }
        loadingRegistrations={
          loadingRegistrations
        }
        fromDate={
          registrationFilters.fromDate
        }
        toDate={
          registrationFilters.toDate
        }
        onFromDateChange={(
          value
        ) =>
          setRegistrationFilters(
            (prev) => ({
              ...prev,
              fromDate: value,
            })
          )
        }
        onToDateChange={(
          value
        ) =>
          setRegistrationFilters(
            (prev) => ({
              ...prev,
              toDate: value,
            })
          )
        }
        showCourseFilter={
          false
        }
        showAdmissionFilter={
          false
        }
        showRegistrationFilter={
          true
        }
        cancellationType=""
        onCancellationTypeChange={() => {}}
        onReset={
          resetRegistrationFilters
        }
        onApply={
          applyRegistrationFilters
        }
      />

      {/* ------------------------------------------------------------------ */}
      {/* COLOR MODAL - ADMISSION                                            */}
      {/* ------------------------------------------------------------------ */}

      <ColorPickerModal reportTitle={"Admission Report"}
        open={
          colorModalOpen ===
          "admission"
        }
        loading={
          pdfLoading
        }
        tempThemeColor={
          tempPdfThemeColor
        }
        tempTextColor={
          tempPdfTextColor
        }
        onClose={() =>
          setColorModalOpen(
            null
          )
        }
        onThemeChange={(
          value
        ) => {
          const next =
            normalizeHex(
              value,
              "#1E293B"
            );

          setTempPdfThemeColor(
            next
          );

          updatePdfThemeColor?.(
            next
          );
        }}
        onTextChange={(
          value
        ) => {
          const next =
            normalizeHex(
              value,
              "#0F172A"
            );

          setTempPdfTextColor(
            next
          );

          updatePdfTextColor?.(
            next
          );
        }}
        onReset={() => {
          setTempPdfThemeColor(
            normalizeHex(
              pdfThemeColor,
              DEFAULT_PDF_COLORS.primary
            )
          );

          setTempPdfTextColor(
            normalizeHex(
              pdfTextColor,
              DEFAULT_PDF_COLORS.bodyText
            )
          );
        }}
        onGenerate={() =>
          generatePdfForReport(
            "admission",
            tempPdfThemeColor,
            tempPdfTextColor
          )
        }
      />

      {/* ------------------------------------------------------------------ */}
      {/* COLOR MODAL - COURSE                                               */}
      {/* ------------------------------------------------------------------ */}

      <ColorPickerModal reportTitle={"Course Report"}
        open={
          colorModalOpen ===
          "course"
        }
        loading={
          pdfLoading
        }
        tempThemeColor={
          tempPdfThemeColor
        }
        tempTextColor={
          tempPdfTextColor
        }
        onClose={() =>
          setColorModalOpen(
            null
          )
        }
        onThemeChange={(
          value
        ) => {
          const next =
            normalizeHex(
              value,
              "#1E293B"
            );

          setTempPdfThemeColor(
            next
          );

          updatePdfThemeColor?.(
            next
          );
        }}
        onTextChange={(
          value
        ) => {
          const next =
            normalizeHex(
              value,
              "#0F172A"
            );

          setTempPdfTextColor(
            next
          );

          updatePdfTextColor?.(
            next
          );
        }}
        onReset={() => {
          setTempPdfThemeColor(
            normalizeHex(
              pdfThemeColor,
              DEFAULT_PDF_COLORS.primary
            )
          );

          setTempPdfTextColor(
            normalizeHex(
              pdfTextColor,
              DEFAULT_PDF_COLORS.bodyText
            )
          );
        }}
        onGenerate={() =>
          generatePdfForReport(
            "course",
            tempPdfThemeColor,
            tempPdfTextColor
          )
        }
      />

      {/* ------------------------------------------------------------------ */}
      {/* COLOR MODAL - CANCELLATION                                         */}
      {/* ------------------------------------------------------------------ */}

      <ColorPickerModal reportTitle={"Admission Cancellation Report"}
        open={
          colorModalOpen ===
          "cancellation"
        }
        loading={
          pdfLoading
        }
        tempThemeColor={
          tempPdfThemeColor
        }
        tempTextColor={
          tempPdfTextColor
        }
        onClose={() =>
          setColorModalOpen(
            null
          )
        }
        onThemeChange={(
          value
        ) => {
          const next =
            normalizeHex(
              value,
              "#1E293B"
            );

          setTempPdfThemeColor(
            next
          );

          updatePdfThemeColor?.(
            next
          );
        }}
        onTextChange={(
          value
        ) => {
          const next =
            normalizeHex(
              value,
              "#0F172A"
            );

          setTempPdfTextColor(
            next
          );

          updatePdfTextColor?.(
            next
          );
        }}
        onReset={() => {
          setTempPdfThemeColor(
            normalizeHex(
              pdfThemeColor,
              DEFAULT_PDF_COLORS.primary
            )
          );

          setTempPdfTextColor(
            normalizeHex(
              pdfTextColor,
              DEFAULT_PDF_COLORS.bodyText
            )
          );
        }}
        onGenerate={() =>
          generatePdfForReport(
            "cancellation",
            tempPdfThemeColor,
            tempPdfTextColor
          )
        }
      />

      {/* ------------------------------------------------------------------ */}
      {/* COLOR MODAL - REGISTRATION                                         */}
      {/* ------------------------------------------------------------------ */}

      <ColorPickerModal reportTitle={"Registration Report"}
        open={
          colorModalOpen ===
          "registration"
        }
        loading={
          pdfLoading
        }
        tempThemeColor={
          tempPdfThemeColor
        }
        tempTextColor={
          tempPdfTextColor
        }
        onClose={() =>
          setColorModalOpen(
            null
          )
        }
        onThemeChange={(
          value
        ) => {
          const next =
            normalizeHex(
              value,
              "#1E293B"
            );

          setTempPdfThemeColor(
            next
          );

          updatePdfThemeColor?.(
            next
          );
        }}
        onTextChange={(
          value
        ) => {
          const next =
            normalizeHex(
              value,
              "#0F172A"
            );

          setTempPdfTextColor(
            next
          );

          updatePdfTextColor?.(
            next
          );
        }}
        onReset={() => {
          setTempPdfThemeColor(
            normalizeHex(
              pdfThemeColor,
              DEFAULT_PDF_COLORS.primary
            )
          );

          setTempPdfTextColor(
            normalizeHex(
              pdfTextColor,
              DEFAULT_PDF_COLORS.bodyText
            )
          );
        }}
        onGenerate={() =>
          generatePdfForReport(
            "registration",
            tempPdfThemeColor,
            tempPdfTextColor
          )
        }
      />

    </Box>
  );
}